import { createClient } from "@supabase/supabase-js";
/* Imported, and re-exported below, rather than left for callers to import
   themselves: an imported module is evaluated before the body of the module
   importing it, so this guarantees the URL is read before createClient() gets
   a chance to consume and scrub the reset fragment. A caller importing
   recovery.js on its own could easily land after this file and see nothing.
   See src/lib/recovery.js for why that matters. */
import { landedOnRecoveryLink } from "./recovery.js";

export { landedOnRecoveryLink };

const url = import.meta.env.VITE_SUPABASE_URL?.trim();
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY?.trim();

/** Reads the `role` claim out of a Supabase API key without verifying it —
 *  enough to tell an anon key from a service_role key. */
function jwtRole(token) {
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64)).role || null;
  } catch {
    return null;
  }
}

/* Vite inlines VITE_* at BUILD time. So on a hosted deploy these values come
   from the build environment, not the running server: changing them on the host
   does nothing until a new build runs. That surprises people, so the setup
   screen says which variable is wrong rather than just "not connected". */
function diagnoseConfig() {
  const issues = [];

  if (!url) {
    issues.push({ key: "VITE_SUPABASE_URL", problem: "Not set in the build environment.", severity: "error" });
  } else if (url.includes("your-project-ref")) {
    issues.push({ key: "VITE_SUPABASE_URL", problem: "Still the placeholder from .env.example.", severity: "error" });
  } else if (!/^https:\/\/.+/i.test(url)) {
    issues.push({ key: "VITE_SUPABASE_URL", problem: `Doesn't start with https:// — got "${url}".`, severity: "error" });
  }

  if (!anonKey) {
    issues.push({ key: "VITE_SUPABASE_ANON_KEY", problem: "Not set in the build environment.", severity: "error" });
  } else if (anonKey.includes("your-anon")) {
    issues.push({ key: "VITE_SUPABASE_ANON_KEY", problem: "Still the placeholder from .env.example.", severity: "error" });
  } else {
    const role = jwtRole(anonKey);
    if (!role) {
      issues.push({ key: "VITE_SUPABASE_ANON_KEY", problem: "Not a valid Supabase API key. Check for a truncated or quoted value.", severity: "error" });
    } else if (role !== "anon") {
      // Refusing to start is the point: a service_role key in a browser bundle
      // hands every visitor full read/write access to every user's data.
      issues.push({
        key: "VITE_SUPABASE_ANON_KEY",
        problem: `This is the "${role}" key, not the anon key. It bypasses row-level security and must never ship to a browser. Replace it with the key labelled "anon public", then rotate this one.`,
        severity: "danger",
      });
    }
  }

  return issues;
}

export const configIssues = diagnoseConfig();

/** False until both variables are present and the key is a real anon key — the
 *  app shows setup instructions instead of pretending to work. There is
 *  deliberately no offline fake-data mode: a screen that looks signed-in
 *  without a real session is the exact problem this build exists to remove. */
export const isSupabaseConfigured = configIssues.length === 0;

export const supabase = isSupabaseConfigured
  ? createClient(url, anonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    })
  : null;

/** Turns a Supabase/Postgres error into something a collector can actually read. */
export function friendlyError(error, fallback = "Something went wrong. Please try again.") {
  if (!error) return null;
  const msg = error.message || String(error);

  if (/Invalid login credentials/i.test(msg)) return "That email and password don't match an account.";
  if (/Email not confirmed/i.test(msg)) return "Check your inbox and confirm your email address first.";
  if (/User already registered/i.test(msg)) return "An account with that email already exists — try signing in.";
  if (/Password should be at least/i.test(msg)) return "Password must be at least 6 characters.";
  // Supabase's built-in email sender allows only a handful of messages per hour
  // on the free tier, so "wait a minute" would be wrong advice here.
  if (/email rate limit/i.test(msg)) {
    return "Supabase's built-in email sender has hit its hourly limit. Wait an hour, or connect your own SMTP provider under Authentication → Emails.";
  }
  const seconds = msg.match(/after (\d+) seconds/i);
  if (seconds) return `Too many attempts — try again in ${seconds[1]} seconds.`;
  if (/rate limit|too many requests/i.test(msg)) return "Too many attempts. Wait a moment and try again.";
  // A broken or missing signup trigger surfaces as an opaque 500 from the auth
  // service; point at the actual cause rather than leaving it cryptic.
  if (/Database error saving new user/i.test(msg)) {
    return "The database rejected the new account. The schema migration probably didn't finish — run supabase/diagnose.sql to check.";
  }
  // PostgREST wording for both "table doesn't exist" and "my cached view of the
  // schema is stale". Neither is something a collector can act on as written.
  // GRANT is a separate gate from row-level security: correct policies still
  // fail if the `authenticated` role was never granted access to the table.
  if (/permission denied for table/i.test(msg)) {
    return "Your account isn't permitted to read this table. The migration's GRANT statements haven't been applied — re-run supabase/migrations/0001_init.sql.";
  }
  if (/schema cache|Could not find the table/i.test(msg)) {
    return "The database tables aren't set up yet. Run supabase/migrations/0001_init.sql in the Supabase SQL Editor — if you already have, run `notify pgrst, 'reload schema';` to refresh the API.";
  }
  /* A follow, block or report points at a row in `profiles`, and every one of
     those foreign keys fails the same way when the other person has no profile
     row yet — which happens when 0001's signup trigger could not be installed
     and that account has not been through the app's own fallback. Postgres
     words it as "violates foreign key constraint follows_followed_id_fkey",
     which tells a collector nothing at all. */
  if (error?.code === "23503" || /violates foreign key constraint/i.test(msg)) {
    return "That collector's profile hasn't finished setting up yet. Ask them to sign out and back in, then try again.";
  }
  // The other half of the same story: your OWN profile row is missing, so
  // anything keyed to you is refused.
  if (/violates row-level security policy/i.test(msg)) {
    return "Your account isn't allowed to do that. If you just signed up, sign out and back in, then try again.";
  }
  if (/Failed to fetch|NetworkError|network/i.test(msg)) return "Can't reach the server. Check your connection.";
  if (/provider is not enabled/i.test(msg)) return "That sign-in method isn't enabled for this project yet.";

  return msg || fallback;
}
